<template>
  <div>
    <NuxtLayout
      name="asuostaticlayout"
      :title="interestArea.title"
      :sub-title="interestArea.subTitle"
      :image-source="interestArea.heroImage"
      :is-header-sticky="false"
      :program-list="RfiDegreeSeekingPrograms"
      :is-sticky-brand-nav="false"
      :is-study-page="true"
    >
      <NavbarStickyAtlas :nav-items="navItems" title="On this page:">
        <template #stuckMenu>
          <div class="d-flex justify-content-end flex-direction-row mb-space-xxxs">
            <ButtonApplyNow
              type="modal"
              event-region="main content"
              event-section="secondary navbar"
            >
            </ButtonApplyNow>
            <button
              class="btn btn-secondary fs-medium fw-bold ms-space-sm"
              @click="smoothScrollTo('hero-rfi-asuo-static')"
            >
              Request info
            </button>
          </div>
        </template>
      </NavbarStickyAtlas>
      <!-- Overview -->
      <SectionApollo
        id="program-intro"
        :title="interestArea.introTitle!"
        :image="interestArea.introImage!"
        class="bg-white"
        image-position="right"
      >
        <div v-html="interestArea.introDescription!"></div>
      </SectionApollo>
      <!-- degree search section -->
      <section
        id="degree-search-section"
        ref="rDegreeSearch"
        class="pt-lg-space-xl pb-space-lg pb-lg-space-xxl bg-white"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 col-lg-9">
              <h2 class="h2-medium mb-space-sm">
                {{ interestArea.degreeSearchTitle }}
              </h2>
              <div
                class="fs-medium pb-space-md d-none d-lg-block"
                v-html="interestArea.degreeSearchDescription"
              ></div>
            </div>
          </div>
          <div id="degree-search">
            <div class="row">
              <div class="col-12 col-lg-5">
                <input
                  v-model="queries"
                  aria-label="Search"
                  type="text"
                  class="form-control mb-space-xs mb-lg-space-lg"
                  placeholder="Search"
                />
              </div>
              <div class="col-12 col-lg-3">
                <select
                  v-model="selectedCategory"
                  class="form-select mb-space-lg"
                  aria-label="Default select example"
                  @change="handleDegreeTypeChange"
                >
                  <option :value="null">Degree type</option>
                  <option
                    v-for="(category, index) in categories"
                    :key="index"
                    :value="category.title"
                  >
                    {{ category.title }}
                  </option>
                </select>
              </div>
            </div>
            <!-- total -->
            <div class="row">
              <div class="col-12">
                <h3 class="h3-medium mb-0 pb-space-sm">
                  {{ resultsFiltered?.length ?? 0 }} programs total
                </h3>
              </div>
              <div v-if="resultsFiltered.length < 1" class="col-12">
                <p>
                  No results found based on the current criteria. Please visit our
                  <NuxtLink
                    to="/online-degree-programs/"
                    class="rds-link"
                    @click.prevent="handleNoResultsLink"
                  >
                    Online Degree Programs
                  </NuxtLink>
                  page for a full list of programs.
                </p>
              </div>
            </div>
            <!-- degree search results -->
            <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3">
              <ClientOnly>
                <NuxtLink
                  v-for="result in paginatedData"
                  :key="result.item.title!"
                  :to="result.item.detailPage!"
                  class="col d-flex"
                  data-cy="online-degree-programs-degree-search-card"
                >
                  <CardDegreeSearch
                    class="flex-fill mb-space-xs mb-lg-space-md mx-0 text-dark-3"
                    border-variant="light-2"
                    :show-label="true"
                    :label="result?.item?.category?.title!"
                    :image-source="result.item.degreeImage!"
                    :image-alt="result.item.title!"
                    label-size="xs"
                    :title="result.item.title!"
                    title-size="medium"
                    :show-description="false"
                    :show-footer="true"
                    :show-cta="true"
                    footer-cta-size="medium"
                    footer-label-size="small"
                    footer-cta-text="Learn more"
                    :footer-label="formattedFooterLabel(result.item.nextStartDate!)"
                    :footer-cta-link="result.item.detailPage!"
                    @card-click="handleCardClick(result.item, $event)"
                    @cta-click="handleCardLinkEvent(result.item, 'learn more', $event)"
                    @icon-click="handleCardLinkEvent(result.item, 'right chevron', $event)"
                  ></CardDegreeSearch>
                </NuxtLink>
              </ClientOnly>
            </div>
            <div v-if="resultsFiltered.length > PAGE_DEFAULTS.PER_PAGE" class="row">
              <RdsPagination
                v-model="currentPage"
                aria-controls=""
                aria-label="Pagination"
                align="center"
                pills
                hide-ellipses
                prev-class="pag-nav-small"
                next-class="pag-nav-small"
                last-class="pag-nav-small"
                first-class="pag-nav-small"
                class="mt-space-md degree-search-pagination px-gutter"
                :per-page="PAGE_DEFAULTS.PER_PAGE"
                first-text="First"
                last-text="Last"
                next-text="Next"
                prev-text="Prev"
                :limit="5"
                size="md"
                :total-rows="resultsFiltered.length"
                @page-click="handlePaginationChange"
              >
              </RdsPagination>
            </div>
          </div>
        </div>
      </section>
      <!-- Careers -->
      <section
        id="study-area-careers"
        class="bg-light-2 py-space-md py-md-space-lg py-lg-space-xxl"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 offset-lg-1 col-lg-11">
              <h2 class="h2-medium mb-space-sm mb-lg-space-md">
                {{ interestArea.skillsCareersTitle }}
              </h2>
              <div class="bg-secondary title-underline mb-space-md"></div>
            </div>
          </div>
          <!-- cards -->
          <div class="row">
            <div class="col-12 col-lg-10 offset-lg-1">
              <CardInfoHorizontal
                v-for="item in interestArea.skillsCareersCards!"
                :key="item?.id"
                :bg-image-source="(item?.image || undefined) as string"
                :image-fit="item?.image ? 'cover' : 'contain'"
                :display-logo="false"
                :display-label="false"
                :title="item?.title!"
                title-level="h3"
                title-size="large"
                :display-cta="false"
                border-variant="#fafafa"
                class="mb-space-xs mb-lg-space-md"
              >
                <div class="mb-space-sm mb-lg-space-md" v-html="item?.text"></div>
              </CardInfoHorizontal>
            </div>
          </div>
        </div>
      </section>
      <!-- Accolades -->
      <SectionApollo
        id="study-area-accolades"
        :title="interestArea.accoladesTitle!"
        :image="interestArea.accoladesImage!"
        image-position="right"
        class="bg-white"
      >
        <div v-html="interestArea.accoladesDescription"></div>
      </SectionApollo>
      <!-- news section -->
      <section
        v-if="interestArea.relatedNewsArticle && interestArea.relatedNewsArticle?.length > 0"
        class="py-space-xs pb-md-space-lg pt-md-space-xs pt-lg-space-md pb-lg-space-xxl bg-white"
      >
        <div class="container">
          <div class="row">
            <div class="col-12">
              <div class="col-12 px-0">
                <h2 class="h2-medium mb-space-sm mb-lg-space-md px-space-xs px-lg-0">
                  {{ interestArea.relatedNewsTitle }}
                </h2>
                <div class="bg-secondary title-underline mb-space-md mx-space-xs mx-lg-0"></div>
              </div>
            </div>
          </div>
          <!-- cards -->
          <div class="row">
            <div
              v-for="item in interestArea.relatedNewsArticle"
              :key="item?.id"
              class="col-12 col-md-6 col-lg-4 mb-space-xs mb-lg-space-sm"
            >
              <CardImageArticle
                class="h-100"
                :clickable-card="true"
                border-variant="light-4"
                :title="item?.title!"
                :image-source="item?.coverImage!"
                :image-alt="item?.coverImageAlt!"
                :date="item?.updatedDate!"
                :minutes="item?.timeToRead!"
                :cta-link="item?.detailPage!"
                @card-click="
                  handleNewsLinkClick(item?.detailPage!, item?.title!, 'read more', $event)
                "
                @cta-click="
                  handleNewsLinkClick(item?.detailPage!, item?.title!, 'read more', $event)
                "
              ></CardImageArticle>
            </div>
          </div>
        </div>
      </section>
      <section
        class="section-bg rds-lazy bg-dark-3 py-space-lg py-md-space-xxl"
        data-background-image="https://cms.asuonline.asu.edu/sites/default/files/dark%20background%20pattern%402x.jpg"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 col-lg-10 offset-lg-1">
              <CardInfoHorizontal
                bg-image-source="https://cms.asuonline.asu.edu/sites/default/files/AreasOfStudy.png"
                image-fit="cover"
                bg-image-alt=""
                image-position="left"
                class="explore-card"
                border-variant="light-4"
                :display-label="false"
                :display-highlight="false"
                title="Explore more degree programs"
                title-level="h2"
                title-size="small"
                :cta-as-button="true"
                cta-button-variant="secondary"
                cta-text-variant="dark-3"
                cta-text-size="medium"
                cta-text-weight="bold"
                cta-text="Browse interest areas"
                cta-link="/study/"
                cta-link-target="_self"
                @cta-click="handleExploreCtaClick($event)"
              >
                <p class="mb-space-sm">
                  ASU Online offers over 350 degrees and certificates in a plethora of fields. Check
                  out other areas of interest to find the right program for you.
                </p>
              </CardInfoHorizontal>
            </div>
          </div>
        </div>
      </section>
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardDegreeSearch } from "@rds-vue-ui/card-degree-search";
import { CardImageArticle } from "@rds-vue-ui/card-image-article";
import { CardInfoHorizontal } from "@rds-vue-ui/card-info-horizontal";
import { NavbarStickyAtlas } from "@rds-vue-ui/navbar-sticky-atlas";
import { SectionApollo } from "@rds-vue-ui/section-apollo";
import { watchDebounced } from "@vueuse/core";
import type { UseFuseOptions } from "@vueuse/integrations/useFuse";
import { useFuse } from "@vueuse/integrations/useFuse";
import type { FuseResult } from "fuse.js";
import ButtonApplyNow from "~/components/nav/ButtonApplyNow.vue";

import type { DegreeSeekingProgram, InterestArea } from "~/server/resolvers/resolvers-types";
import type { StudyBySlugQuery } from "~/types/graphql-operations";
const schemaService = useSchemaService();
const route = useRoute();
const siteUrl = useRuntimeConfig().public.siteUrl;
/*
********************************************
Page quires
********************************************
*/
const $page = await usePageQuery<StudyBySlugQuery>(
  /* GraphQL */ `
    #graphql
    query StudyBySlug($slug: String!, $filter: filterInput, $sort: sortInput) {
      allCategory {
        id
        title
      }
      interestArea(slug: $slug) {
        id
        metaTitle
        metaDescription
        categoryBySlug {
          slug
          detailPage
          title
          id
        }
        title
        subTitle
        heroImage
        introTitle
        introDescription
        introImage
        degreeSearchTitle
        degreeSearchDescription
        skillsCareersTitle
        skillsCareersCards {
          id
          title
          text
          image
        }
        accoladesTitle
        accoladesDescription
        accoladesImage
        relatedNewsTitle
        relatedNewsArticle {
          id
          title
          detailPage
          coverImage
          coverImageAlt
          updatedDate
          timeToRead
        }
      }
      allDegreeSeekingProgram(sort: $sort) {
        id
        code
        title
        alternateTitle
        shortDescription
        detailPage
        degreeImage
        nextStartDate
        detailPage
        interestAreas {
          id
        }
        category {
          title
        }
      }
      RfiDegreeSeekingPrograms: allDegreeSeekingProgram(filter: $filter, sort: $sort) {
        id
        code
        title
        shortDescription
        detailPage
        degreeImage
        nextStartDate
        detailPage
        category {
          title
        }
        interestAreas {
          id
        }
      }
    }
  `,
  {
    slug: route.params.slug as string,
    filter: {
      programFeature: {
        eq: "b4e8dd15-fed3-4594-a24b-ad8147dc9e26"
      }
    },
    sort: {
      by: "title",
      order: "ASC"
    }
  },
  { key: route.params.slug as string }
);

// `interestArea` is nullable in the schema; this page renders it
// unconditionally throughout, so a missing interest area is a controlled
// error rather than a silent crash.
if (!$page?.interestArea) {
  throw createError({ message: `${String(useRoute().name)} query failed` });
}

const interestArea = $page.interestArea;
const categories = ($page.allCategory ?? []).filter(
  (category): category is NonNullable<typeof category> => category !== null
);

const taxonomy = interestArea.categoryBySlug?.id;

// generateInterestAreaTemplateSchema and the Fuse search below are typed
// against DegreeSeekingProgram (the full resolver schema), but only read
// fields this query selects -- see useSchemaService.ts. The structural
// mismatch on unselected required fields (id aside; costAcademicYear,
// latestAcademicYear, etc.) that nothing here reads is safe to bridge.
//
// modify allDegreeSeekingProgram by filtering the value based on interest area
const allDegreeSeekingProgram = ($page.allDegreeSeekingProgram ?? [])
  .filter((program) => program !== null)
  .filter((program) =>
    (program.interestAreas ?? []).some((area) => area?.id === (taxonomy as string))
  ) as unknown as DegreeSeekingProgram[];

const RfiDegreeSeekingPrograms = ($page.RfiDegreeSeekingPrograms ?? [])
  .filter((program) => program !== null)
  .filter((program) =>
    (program.interestAreas ?? []).some((area) => area?.id === (taxonomy as string))
  ) as unknown as DegreeSeekingProgram[];
/*
********************************************
Page meta tags
********************************************
*/
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"]
  }
});

useHead({
  title: interestArea.metaTitle,
  meta: [
    {
      name: "Title",
      content: interestArea.metaTitle
    },
    {
      name: "Description",
      content: interestArea.metaDescription
    },
    ...useUtilityService().generateSocialTags(
      interestArea.metaTitle as string,
      interestArea.metaDescription as string,
      interestArea.heroImage as string
    )
  ],
  link: [
    {
      rel: "canonical",
      href: `${siteUrl}${interestArea.categoryBySlug?.detailPage}`
    }
  ],
  script: [
    schemaService.generateInterestAreaTemplateSchema(
      interestArea as unknown as InterestArea,
      allDegreeSeekingProgram
    )
  ]
});
/*
********************************************
Page data
********************************************
*/

const navItems = {
  "program-intro": "Overview",
  "degree-search-section": "Degrees",
  "study-area-careers": "Careers",
  "study-area-accolades": "Accolades"
};

const formattedSection = computed<string | null>(() => {
  return interestArea.degreeSearchTitle!.toLowerCase() || null;
});

const formattedText = computed(() => {
  return selectedCategory.value?.toLowerCase() || null;
});

/*
********************************************
Methods
********************************************
*/
const smoothScrollTo = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
    analyticsComposable.trackEvent({
      event: "link",
      name: "onclick",
      action: "click",
      type: "internal link",
      region: "navbar",
      section: "sticky navbar",
      text: "request info",
      component: "sticky navbar"
    });
  }
};
const handleNoResultsLink = () => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    interestArea.degreeSearchTitle!.toLowerCase() as string,
    "online degree programs",
    "degree search"
  );
};
const formattedFooterLabel = (nextStartDate: string) => {
  return `Next start date: ${nextStartDate}`;
};
/*
********************************************
Fuse search
********************************************
*/
const queries = ref<string | null>(null);
const selectedCategory = ref<string | null>(null);

const fuseOptions: UseFuseOptions<DegreeSeekingProgram> = {
  fuseOptions: {
    // set minMatchCharLength to 1 otherwise search bar will not filter programs with long title
    isCaseSensitive: false,
    includeScore: true,
    useExtendedSearch: true,
    includeMatches: true,
    minMatchCharLength: 1,
    shouldSort: true,
    findAllMatches: false,
    location: 0,
    threshold: 0.6,
    distance: 200,
    keys: ["title", "category.title"]
  },
  matchAllWhenSearchEmpty: true
};
const searchOptions = ref<DegreeSeekingProgram[]>(allDegreeSeekingProgram);
// logic to send query input to data layer after slight delay
watchDebounced(
  queries,
  () => {
    analyticsComposable.trackEvent({
      event: "search",
      name: "onenter",
      action: "type",
      type: "degree search",
      region: "main content",
      section: formattedSection.value || "null",
      text: formattedText.value || "N/A",
      component: "degrees"
    });
  },
  { debounce: 1500 }
);
// logic to ensure page number is never greater than length of pages, or 0. watching search string and category
const keepPageInRange = () => {
  if (currentPage.value > Math.ceil(resultsFiltered.value.length / PAGE_DEFAULTS.PER_PAGE)) {
    currentPage.value = Math.ceil(resultsFiltered.value.length / PAGE_DEFAULTS.PER_PAGE);
  } else if (currentPage.value === 0) {
    currentPage.value = 1;
  }
};
watch(queries, () => keepPageInRange(), { flush: "post" });
watch(selectedCategory, () => keepPageInRange(), { flush: "post" });

const resultsFiltered = ref<FuseResult<DegreeSeekingProgram>[]>([]);
const { fuse, results } = useFuse("", searchOptions, fuseOptions);
resultsFiltered.value = results.value;
const preparedSchema = computed(() => {
  const schema = [];
  if (selectedCategory.value) {
    schema.push({
      $or: [{ "category.title": `=${selectedCategory.value}` }]
    });
  }
  if (queries.value) {
    schema.push({
      $or: [{ title: `~${queries.value}` }]
    });
  }
  return schema;
});

enum PAGE_DEFAULTS {
  PER_PAGE = 12
}
const rDegreeSearch = ref<HTMLElement | null>(null);
const currentPage = ref<number>(1);
const paginatedData = ref<FuseResult<DegreeSeekingProgram>[]>([]);
const fetchData = () => {
  const start = (currentPage.value - 1) * PAGE_DEFAULTS.PER_PAGE;
  const end = start + PAGE_DEFAULTS.PER_PAGE;
  paginatedData.value = resultsFiltered.value.slice(start, end);
};

const handlePaginationChange = (event: Event, page: number) => {
  const section = interestArea.degreeSearchTitle!;
  const label = (event.target as HTMLElement).innerText;
  const text = !isNaN(parseInt(label)) ? `page ${page}` : label.toLocaleLowerCase();
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "pagination",
    "main content",
    section.toLowerCase(),
    text,
    "degrees"
  );
  fetchData();
  rDegreeSearch.value?.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest"
  });
};
watchEffect(() => {
  if (preparedSchema.value.length > 0) {
    const results = fuse.value.search({
      $and: preparedSchema.value
    });
    resultsFiltered.value = results;
    fetchData();
  } else {
    resultsFiltered.value = results.value;
    fetchData();
  }
});
const handleNewsLinkClick = (detailPage: string, title: string, component: string, e: Event) => {
  e.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    title.toLowerCase(),
    formattedSection.value as string,
    component.toLowerCase()
  );
  navigateTo(detailPage, {
    open: {
      target: "_self"
    }
  });
};
const handleExploreCtaClick = (event: Event) => {
  event.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "explore more degree programs",
    "browse interest areas",
    "card - info horizontal"
  );
  navigateTo("/study/", {
    open: {
      target: "_self"
    }
  });
};

const handleDegreeTypeChange = () => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "select degree type",
    "main content",
    formattedSection.value as string,
    formattedText.value || "select degree type",
    "degrees"
  );
};
const handleCardClick = (item: DegreeSeekingProgram, event: Event) => {
  event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    item?.title?.toLowerCase() || "null",
    "learn more",
    "degree search"
  );
  navigateTo(item.detailPage, {
    open: {
      target: "_self"
    }
  });
};
const handleCardLinkEvent = (card: DegreeSeekingProgram, component: string, event: Event) => {
  event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    formattedSection.value || "null",
    `${card?.title?.toLowerCase()} ${component}`.toLocaleLowerCase(),
    "degrees"
  );
  navigateTo(card.detailPage, {
    open: {
      target: "_self"
    }
  });
};
</script>

<style lang="scss" scoped>
.section-bg {
  background-size: auto;
  background-position: top;
  background-repeat: repeat;
}
.explore-card {
  max-width: 920px;
  margin-inline: auto;
}
@media (min-width: 768px) {
  .explore-card :deep(.bg-image.image-fit-cover) {
    min-height: 0;
  }
}
@media (min-width: 992px) {
  .explore-card :deep(.cih-body) {
    padding: 3rem !important;
  }
}
@media (max-width: 576px) {
  :deep(.pag-nav-small button) {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
}

.title-underline {
  width: 64px;
  height: 5px;
}
.degree-search-pagination {
  --rds-pagination-color: var(--rds-dark-3);
  --rds-pagination-bg: transparent;
  --rds-pagination-border-radius: 50%;
  --rds-pagination-border-width: 0rem;
  --rds-pagination-border-color: transparent;
  --rds-pagination-hover-bg: var(--rds-primary);
  --rds-pagination-hover-color: var(--rds-white);
  --rds-pagination-hover-border-color: transparent;
  --rds-pagination-active-color: var(--rds-white);
  --rds-pagination-active-bg: var(--rds-primary);
  --rds-pagination-active-border-color: transparent;
  --rds-pagination-disabled-color: var(--rds-light-4);
  --rds-pagination-disabled-bg: transparent;
  --rds-pagination-disabled-border-color: transparent;
  --rds-pagination-focus-color: var(--rds-dark-3);
  --rds-pagination-focus-bg: transparent;
  --rds-pagination-padding-y: 0.5rem;
}
</style>
